import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const handleCTAClick = () => {
    window.open('https://pay.monetizze.com.br/KRA456690', '_blank');
  };

  return (
    <section ref={ref} className="py-20 sm:py-32 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-glow-pulse" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Price Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary via-secondary to-primary rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity" />
            
            <div className="relative bg-card border-2 border-primary/30 rounded-3xl p-8 sm:p-12 text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 backdrop-blur-sm border border-primary/20 rounded-full mb-8">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Oferta Especial</span>
              </div>

              {/* Headline */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Tudo isso por apenas
              </h2>

              {/* Price */}
              <div className="mb-8">
                <div className="inline-block">
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-6xl sm:text-7xl lg:text-8xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                      R$ 29
                    </span>
                    <span className="text-4xl sm:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                      ,99
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm">Pagamento único • Acesso vitalício</p>
                </div>
              </div>

              {/* Value proposition */}
              <div className="mb-10">
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
                  Menos do que o preço de um almoço, para{' '}
                  <span className="text-primary font-semibold">transformar sua vida financeira</span>
                </p>
              </div>

              {/* CTA Button */}
              <Button
                onClick={handleCTAClick}
                size="lg"
                className="group relative overflow-hidden bg-primary hover:bg-primary-glow text-primary-foreground px-16 py-7 text-xl font-bold rounded-xl shadow-glow hover:shadow-glow-strong transition-all duration-300 hover:scale-105 w-full sm:w-auto"
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  EU QUERO
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-glow to-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Button>

              {/* Guarantees */}
              <div className="mt-10 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-success rounded-full" />
                  <span>Pagamento Seguro</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-success rounded-full" />
                  <span>Acesso Imediato</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-success rounded-full" />
                  <span>Garantia 7 Dias</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Social proof */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <p className="text-muted-foreground">
              + de <span className="text-primary font-semibold">5.000 pessoas</span> já transformaram suas finanças
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
